import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router";
import {
  Button,
} from "@nosible/shared/components/ui/button";
import { useSession, authClient } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import {
  ArrowLeft,
  ArrowUpRight,
} from "lucide-react";
import { SubApiKeys } from "@/account/components/SubApiKeys";
import { SubCurrentUsage } from "@/account/components/subscriptions/SubCurrentUsage";
import { SubTotalUsage } from "@/account/components/subscriptions/SubTotalUsage";
import { CancelSubscription } from "@/account/components/CancelSubscription";
import { SubscriptionHistoryDialog } from "@/account/components/SubscriptionHistoryDialog";
import { AccountPageHeader } from "@/account/components/AccountPageHeader";

export function SubscriptionPage() {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { data: session } = useSession();
  const [canAdminUpgrade, setCanAdminUpgrade] = useState<boolean | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");

  const {
    data: subscription,
    isLoading,
    error,
  } = trpc.subscription.getSubscription.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId }
  );

  const { data: organizations } = trpc.organization.list.useQuery(undefined, {
    enabled: !!session,
  });

  const { data: apiKeys } = trpc.subscription.apiKey.listApiKeys.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId },
  );

  const { data: currentUsage } = trpc.subscription.usage.currentUsage.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId },
  );

  const { data: totalUsageHistory } = trpc.subscription.usage.totalUsageHistory.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId },
  );

  const transferToOrganization = trpc.subscription.mutation.transferToOrganization.useMutation({
    onSuccess: () => window.location.reload(),
  });

  useEffect(() => {
    const checkPermission = async () => {
      if (!session?.user) {
        setCanAdminUpgrade(false);
        return;
      }

      try {
        const { data } = await authClient.admin.hasPermission({
          permission: {
            subscription: ["create"],
          },
        });
        setCanAdminUpgrade(data?.success ?? false);
      } catch {
        setCanAdminUpgrade(false);
      }
    };

    checkPermission();
  }, [session]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-red-400">{error.message}</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-chromium-mist">Subscription not found</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const usageSplits = currentUsage?.usage?.splits ?? [];
  const requestsThisPeriod = usageSplits.reduce((total, entry) => total + entry.used, 0);
  const periodLimit = usageSplits.reduce((total, entry) => total + entry.limit, 0);
  const ownerInitials = (subscription.owner?.name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="account-ui-page subscription-page">
      <AccountPageHeader
        title={subscription.plan.name}
        description={subscription.owner?.type === "user" ? "Personal subscription" : "Organization subscription"}
        titleAdornment={<span className={`account-role account-role--${subscription.status}`}>{subscription.status.toUpperCase()}</span>}
        actions={
          <div className="subscription-page__actions">
            {(subscription.status === "active" || subscription.status === "trialing") && (
              <>
                <Button asChild variant="outline" size="sm">
                  <Link to={`/subscriptions/${subscriptionId}/upgrade`}>CHANGE PLAN</Link>
                </Button>
                <CancelSubscription
                  subscriptionId={subscriptionId!}
                  onSuccess={() => window.location.reload()}
                />
              </>
            )}
          </div>
        }
      />

      <div className="subscription-page__content">
        <div className="account-metrics-strip subscription-page__metrics" aria-label="Subscription summary">
          <div><span>API KEYS</span><strong>{apiKeys?.length ?? 0}</strong></div>
          <div><span>THIS PERIOD</span><strong>{requestsThisPeriod.toLocaleString()}</strong></div>
          <div><span>PERIOD LIMIT</span><strong>{periodLimit.toLocaleString()}</strong></div>
          <div><span>ALL TIME</span><strong>{totalUsageHistory?.currentTotal?.toLocaleString() ?? "0"}</strong></div>
        </div>

        <SubCurrentUsage subscriptionId={subscriptionId!} />
        <SubApiKeys subscriptionId={subscriptionId!} />
        <SubTotalUsage subscriptionId={subscriptionId!} />

        <section className="subscription-page__details" aria-labelledby="subscription-details-heading">
          <div className="subscription-details__heading subscription-section-heading">
            <span id="subscription-details-heading">04 DETAILS</span>
            <div className="subscription-details__actions">
              <SubscriptionHistoryDialog
                subscriptionId={subscriptionId!}
                subscriptionName={subscription.plan.name}
              />
              {(subscription.status === "active" || subscription.status === "trialing") && (
                <>
                  <CancelSubscription
                    subscriptionId={subscriptionId!}
                    onSuccess={() => window.location.reload()}
                  />
                  <Button asChild className="subscription-details__upgrade">
                    <Link to={`/subscriptions/${subscriptionId}/upgrade`}>
                      <ArrowUpRight aria-hidden="true" />
                      UPGRADE
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="subscription-details__body">
            <div className="subscription-details__summary">
              <div className="subscription-details__cell subscription-details__owner-cell">
                <span className="subscription-details__label">OWNER</span>
                <div className="subscription-details__owner">
                  <span className="subscription-details__avatar" aria-hidden="true">
                    {ownerInitials}
                  </span>
                  <span>
                    <strong>{subscription.owner?.name || "Unknown"}</strong>
                    <small>{subscription.owner?.type === "organization" ? "ORGANIZATION" : "PERSONAL"}</small>
                  </span>
                </div>
              </div>
              <div className="subscription-details__cell">
                <span className="subscription-details__label">PLAN</span>
                <strong className="subscription-details__value">{subscription.plan?.name}</strong>
              </div>
              <div className="subscription-details__cell">
                <span className="subscription-details__label">STATUS</span>
                <strong className="subscription-details__value subscription-details__status">
                  <i aria-hidden="true" />
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </strong>
              </div>
              <div className="subscription-details__cell">
                <span className="subscription-details__label">PERIOD END</span>
                <strong className="subscription-details__value">
                  {subscription.periodEnd
                    ? new Date(subscription.periodEnd).toLocaleDateString()
                    : "Indefinite"}
                </strong>
              </div>
            </div>

            {subscription.owner?.type === "user" && subscription.owner.id === session?.user?.id && organizations && organizations.some((org) => org.role === "owner" || org.role === "admin") && (
              <div className="subscription-details__move">
                <div className="subscription-details__move-copy">
                  <h3>Move to an organization</h3>
                  <p>This keeps the billing record and API keys together while giving the organization ownership.</p>
                </div>
                <div className="subscription-details__move-controls">
                  <select
                    value={selectedOrganizationId}
                    onChange={(event) => setSelectedOrganizationId(event.target.value)}
                    aria-label="Organization to receive subscription"
                  >
                    <option value="">Select an organization</option>
                    {organizations
                      .filter((org) => org.role === "owner" || org.role === "admin")
                      .map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
                  </select>
                  <Button
                    size="sm"
                    disabled={!selectedOrganizationId || transferToOrganization.isPending}
                    onClick={() => transferToOrganization.mutate({ subscriptionId: subscription.id, organizationId: selectedOrganizationId })}
                  >
                    {transferToOrganization.isPending ? "MOVING..." : "MOVE SUBSCRIPTION"}
                  </Button>
                </div>
                {transferToOrganization.error && <p className="subscription-details__move-error">{transferToOrganization.error.message}</p>}
              </div>
            )}

            {subscription.status === "canceled" && (
              <div className="subscription-details__canceled">
                This subscription has been canceled. API keys have been deactivated.
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
}
